import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiCheckCircle,
    FiClock,
    FiEye,
    FiFileText,
    FiLoader,
    FiPlus,
    FiRefreshCw,
    FiXCircle,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchDebitNotesByPurchaseBill,
    purchaseBillDebitNotesSelector,
    debitNoteLoadingSelector,
    debitNoteErrorSelector,
} from "../../../../../../../store/billing/debitNotes/debitNoteSlice";

import CreateDebitNoteDialog from "./CreateDebitNoteDialog";
import DebitNoteDetailsDialog from "./DebitNoteDetailsDialog";
import "../DebitNotes.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
        100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    ).format(date);
};

const formatLabel = (
    value
) =>
    String(
        value || "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );

const getPurchaseBillId = (
    purchaseBill
) => {
    if (!purchaseBill) {
        return "";
    }

    if (
        typeof purchaseBill
            ?.purchaseBill ===
        "object"
    ) {
        return (
            purchaseBill
                ?.purchaseBill
                ?._id ||
            purchaseBill
                ?.purchaseBill
                ?.id ||
            ""
        );
    }

    return (
        purchaseBill?._id ||
        purchaseBill?.id ||
        purchaseBill?.purchaseBill ||
        ""
    );
};

const getPurchaseBillNumber = (
    purchaseBill
) =>
    purchaseBill?.documentNumber ||
    purchaseBill?.purchaseBillNumber ||
    "-";

const getDebitNoteNumber = (
    note
) =>
    note?.debitNoteNumber ||
    note?.documentNumber ||
    "-";

const getDebitNoteAmount = (
    note
) =>
    roundMoney(
        note
            ?.totals
            ?.debitAmount ??
        note
            ?.totals
            ?.grandTotal ??
        note
            ?.debitAmount ??
        note
            ?.totalAmount ??
        note
            ?.amount ??
        0
    );

const getDebitNoteStatus = (
    note
) =>
    String(
        note?.documentStatus ||
        note?.status ||
        "DRAFT"
    ).toUpperCase();

const getDebitNoteReason = (
    note
) =>
    note?.reason ||
    note?.reasonCode ||
    "-";

const getDebitNoteItemCount = (
    note
) => {
    if (
        Array.isArray(
            note?.items
        )
    ) {
        return note.items.length;
    }

    return toNumber(
        note?.itemCount
    );
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const DebitNotePanel = ({
    purchaseBill,
    onPurchaseBillUpdated,
    onViewDebitNote,
}) => {
    const dispatch =
        useDispatch();

    /*
    |--------------------------------------------------------------------------
    | REDUX
    |--------------------------------------------------------------------------
    */

    const debitNotesState =
        useSelector(
            purchaseBillDebitNotesSelector
        );

    const loading =
        useSelector(
            debitNoteLoadingSelector
        );

    const error =
        useSelector(
            debitNoteErrorSelector
        );

    /*
    |--------------------------------------------------------------------------
    | NORMALIZE DEBIT NOTES
    |--------------------------------------------------------------------------
    |
    | Handles slice values like:
    |
    | []
    |
    | or:
    |
    | {
    |     records: []
    | }
    |
    | or:
    |
    | {
    |     data: []
    | }
    |--------------------------------------------------------------------------
    */

    const debitNotes =
        useMemo(
            () => {
                if (
                    Array.isArray(
                        debitNotesState
                    )
                ) {
                    return debitNotesState;
                }

                if (
                    Array.isArray(
                        debitNotesState
                            ?.records
                    )
                ) {
                    return debitNotesState.records;
                }

                if (
                    Array.isArray(
                        debitNotesState
                            ?.data
                    )
                ) {
                    return debitNotesState.data;
                }

                return [];
            },
            [
                debitNotesState,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | NORMALIZE LOADING
    |--------------------------------------------------------------------------
    */

    const isListLoading =
        typeof loading ===
            "boolean"
            ? loading
            : Boolean(
                loading?.list ||
                loading?.purchaseBill ||
                loading?.purchaseBillDebitNotes ||
                loading?.fetchByPurchaseBill ||
                false
            );

    const isCreating =
        typeof loading ===
            "object"
            ? Boolean(
                loading?.create ||
                loading?.confirm
            )
            : false;

    /*
    |--------------------------------------------------------------------------
    | NORMALIZE ERROR
    |--------------------------------------------------------------------------
    */

    const listError =
        typeof error ===
            "string"
            ? error
            : error?.list ||
            error?.purchaseBill ||
            error?.purchaseBillDebitNotes ||
            error?.fetchByPurchaseBill ||
            null;

    /*
    |--------------------------------------------------------------------------
    | LOCAL STATE
    |--------------------------------------------------------------------------
    */

    const [
        dialogOpen,
        setDialogOpen,
    ] = useState(false);

    const [
        localError,
        setLocalError,
    ] = useState("");

    /*
|--------------------------------------------------------------------------
| DEBIT NOTE DETAILS DIALOG
|--------------------------------------------------------------------------
*/

    const [
        detailsDialogOpen,
        setDetailsDialogOpen,
    ] = useState(false);

    const [
        selectedDebitNote,
        setSelectedDebitNote,
    ] = useState(null);
    /*
    |--------------------------------------------------------------------------
    | PURCHASE BILL
    |--------------------------------------------------------------------------
    */

    const purchaseBillId =
        getPurchaseBillId(
            purchaseBill
        );

    const purchaseBillStatus =
        String(
            purchaseBill
                ?.documentStatus ||
            purchaseBill
                ?.status ||
            ""
        ).toUpperCase();

    const canCreateDebitNote =
        purchaseBillStatus ===
        "CONFIRMED";

    /*
    |--------------------------------------------------------------------------
    | LOAD DEBIT NOTES
    |--------------------------------------------------------------------------
    */

    const loadDebitNotes =
        useCallback(
            async () => {
                if (
                    !purchaseBillId
                ) {
                    return;
                }

                try {
                    setLocalError("");

                    await dispatch(
                        fetchDebitNotesByPurchaseBill({
                            purchaseBillId,
                            page: 1,
                            limit: 100,
                        })
                    ).unwrap();
                } catch (
                fetchError
                ) {
                    setLocalError(
                        typeof fetchError ===
                            "string"
                            ? fetchError
                            : fetchError
                                ?.message ||
                            "Unable to load debit notes."
                    );
                }
            },
            [
                dispatch,
                purchaseBillId,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | INITIAL LOAD
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (
                !purchaseBillId
            ) {
                return;
            }

            loadDebitNotes();
        },
        [
            purchaseBillId,
            loadDebitNotes,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | SUMMARY
    |--------------------------------------------------------------------------
    |
    | No purchaseBillDebitNotesSummarySelector required.
    |--------------------------------------------------------------------------
    */

    const confirmedDebitNotes =
        useMemo(
            () =>
                debitNotes.filter(
                    (
                        note
                    ) =>
                        getDebitNoteStatus(
                            note
                        ) ===
                        "CONFIRMED"
                ),
            [
                debitNotes,
            ]
        );

    const draftDebitNotes =
        useMemo(
            () =>
                debitNotes.filter(
                    (
                        note
                    ) =>
                        getDebitNoteStatus(
                            note
                        ) ===
                        "DRAFT"
                ),
            [
                debitNotes,
            ]
        );

    const cancelledDebitNotes =
        useMemo(
            () =>
                debitNotes.filter(
                    (
                        note
                    ) =>
                        getDebitNoteStatus(
                            note
                        ) ===
                        "CANCELLED"
                ),
            [
                debitNotes,
            ]
        );

    const totalDebitNotes =
        debitNotes.length;

    const confirmedCount =
        confirmedDebitNotes.length;

    const draftCount =
        draftDebitNotes.length;

    const cancelledCount =
        cancelledDebitNotes.length;

    const confirmedAmount =
        useMemo(
            () =>
                roundMoney(
                    confirmedDebitNotes.reduce(
                        (
                            total,
                            note
                        ) =>
                            total +
                            getDebitNoteAmount(
                                note
                            ),
                        0
                    )
                ),
            [
                confirmedDebitNotes,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | CREATE SUCCESS
    |--------------------------------------------------------------------------
    */

    const handleDebitNoteCreated =
        async (
            debitNote
        ) => {
            setDialogOpen(
                false
            );

            /*
            |--------------------------------------------------------------------------
            | REFRESH DEBIT NOTES
            |--------------------------------------------------------------------------
            */

            await loadDebitNotes();

            /*
            |--------------------------------------------------------------------------
            | REFRESH PURCHASE BILL
            |--------------------------------------------------------------------------
            */

            if (
                onPurchaseBillUpdated
            ) {
                await onPurchaseBillUpdated(
                    debitNote
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | VIEW DEBIT NOTE
    |--------------------------------------------------------------------------
    */

    /*
    |--------------------------------------------------------------------------
    | VIEW DEBIT NOTE
    |--------------------------------------------------------------------------
    */

    const handleView = (
        note
    ) => {
        if (!note) {
            return;
        }

        setSelectedDebitNote(
            note
        );

        setDetailsDialogOpen(
            true
        );

        /*
        |--------------------------------------------------------------------------
        | OPTIONAL PARENT CALLBACK
        |--------------------------------------------------------------------------
        |
        | Keep this only if parent needs to know which debit note was clicked.
        | The dialog itself is handled locally.
        |--------------------------------------------------------------------------
        */

        if (
            typeof onViewDebitNote ===
            "function"
        ) {
            onViewDebitNote(
                note
            );
        }
    };

    /*
    |--------------------------------------------------------------------------
    | CLOSE DETAILS DIALOG
    |--------------------------------------------------------------------------
    */

    const handleCloseDetailsDialog =
        () => {
            setDetailsDialogOpen(
                false
            );

            setSelectedDebitNote(
                null
            );
        };

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="debit-notes-panel">

            {/* =====================================================
            HEADER
        ===================================================== */}

            <div className="debit-notes-panel-header">

                <div className="debit-notes-panel-heading">

                    <div className="debit-notes-panel-heading-icon">
                        <FiFileText />
                    </div>

                    <div>
                        <h3>Debit Notes</h3>

                        <p>
                            View purchase returns and debit adjustments
                            created against this purchase bill.
                        </p>
                    </div>

                </div>


                <div className="debit-notes-panel-actions">

                    <button
                        type="button"
                        className="debit-note-button"
                        disabled={isListLoading}
                        onClick={loadDebitNotes}
                    >
                        <FiRefreshCw
                            className={
                                isListLoading
                                    ? "debit-note-spinner"
                                    : ""
                            }
                        />

                        Refresh
                    </button>


                    <button
                        type="button"
                        className="
                        debit-note-button
                        debit-note-button-primary
                    "
                        disabled={
                            !canCreateDebitNote ||
                            isCreating
                        }
                        onClick={() =>
                            setDialogOpen(true)
                        }
                    >
                        <FiPlus />

                        Create Debit Note
                    </button>

                </div>

            </div>


            {/* =====================================================
            PURCHASE BILL NOT CONFIRMED
        ===================================================== */}

            {!canCreateDebitNote && (
                <div className="debit-note-info">

                    <FiClock />

                    <span>
                        Confirm purchase bill{" "}

                        <strong>
                            {getPurchaseBillNumber(
                                purchaseBill
                            )}
                        </strong>

                        {" "}before creating a debit note.
                    </span>

                </div>
            )}


            {/* =====================================================
            ERROR
        ===================================================== */}

            {(localError || listError) && (
                <div className="debit-note-alert">
                    {localError || listError}
                </div>
            )}


            {/* =====================================================
            SUMMARY
        ===================================================== */}

            <div className="debit-note-summary-grid">

                {/* TOTAL */}

                <div className="
                debit-note-summary-card
                debit-note-summary-card-total
            ">
                    <div className="debit-note-summary-card-top">

                        <div className="
                        debit-note-summary-icon
                        debit-note-summary-icon-total
                    ">
                            <FiFileText />
                        </div>

                        <span>
                            Total Debit Notes
                        </span>

                    </div>

                    <strong>
                        {totalDebitNotes}
                    </strong>

                    <small>
                        All debit notes
                    </small>
                </div>


                {/* CONFIRMED */}

                <div className="
                debit-note-summary-card
                debit-note-summary-card-confirmed
            ">
                    <div className="debit-note-summary-card-top">

                        <div className="
                        debit-note-summary-icon
                        debit-note-summary-icon-confirmed
                    ">
                            <FiCheckCircle />
                        </div>

                        <span>
                            Confirmed
                        </span>

                    </div>

                    <strong>
                        {confirmedCount}
                    </strong>

                    <small>
                        Posted adjustments
                    </small>
                </div>


                {/* DRAFT */}

                <div className="
                debit-note-summary-card
                debit-note-summary-card-draft
            ">
                    <div className="debit-note-summary-card-top">

                        <div className="
                        debit-note-summary-icon
                        debit-note-summary-icon-draft
                    ">
                            <FiClock />
                        </div>

                        <span>
                            Draft
                        </span>

                    </div>

                    <strong>
                        {draftCount}
                    </strong>

                    <small>
                        Pending confirmation
                    </small>
                </div>


                {/* CANCELLED */}

                <div className="
                debit-note-summary-card
                debit-note-summary-card-cancelled
            ">
                    <div className="debit-note-summary-card-top">

                        <div className="
                        debit-note-summary-icon
                        debit-note-summary-icon-cancelled
                    ">
                            <FiXCircle />
                        </div>

                        <span>
                            Cancelled
                        </span>

                    </div>

                    <strong>
                        {cancelledCount}
                    </strong>

                    <small>
                        Cancelled debit notes
                    </small>
                </div>


                {/* CONFIRMED DEBIT */}

                <div className="
                debit-note-summary-card
                debit-note-summary-card-amount
            ">
                    <div className="debit-note-summary-card-top">

                        <div className="
                        debit-note-summary-icon
                        debit-note-summary-icon-amount
                    ">
                            <FiFileText />
                        </div>

                        <span>
                            Confirmed Debit
                        </span>

                    </div>

                    <strong>
                        {formatCurrency(
                            confirmedAmount
                        )}
                    </strong>

                    <small>
                        Supplier payable adjustment
                    </small>
                </div>

            </div>


            {/* =====================================================
            DEBIT NOTE LIST CARD
        ===================================================== */}

            <div className="debit-notes-panel-card">

                {/* CARD HEADER */}

                <div className="debit-notes-panel-card-header">

                    <div>
                        <h4>
                            Debit Note History
                        </h4>

                        <p>
                            Debit notes created against{" "}

                            <strong>
                                {getPurchaseBillNumber(
                                    purchaseBill
                                )}
                            </strong>
                        </p>
                    </div>


                    <span className="debit-notes-record-count">
                        {totalDebitNotes}{" "}
                        {totalDebitNotes === 1
                            ? "Record"
                            : "Records"}
                    </span>

                </div>


                {/* =================================================
                LOADING
            ================================================= */}

                {isListLoading &&
                    debitNotes.length === 0 ? (

                    <div className="debit-note-empty">

                        <div className="debit-note-empty-icon">
                            <FiLoader className="debit-note-spinner" />
                        </div>

                        <h4>
                            Loading debit notes...
                        </h4>

                        <p>
                            Please wait while debit notes are loaded.
                        </p>

                    </div>

                ) : debitNotes.length === 0 ? (

                    /* =============================================
                        EMPTY
                    ============================================= */

                    <div className="debit-note-empty">

                        <div className="debit-note-empty-icon">
                            <FiFileText />
                        </div>

                        <h4>
                            No Debit Notes Yet
                        </h4>

                        <p>
                            No purchase return or debit adjustment has
                            been created against this purchase bill.
                        </p>


                        {canCreateDebitNote && (

                            <button
                                type="button"
                                className="
                                debit-note-button
                                debit-note-button-primary
                                debit-note-empty-create-button
                            "
                                disabled={isCreating}
                                onClick={() =>
                                    setDialogOpen(true)
                                }
                            >
                                <FiPlus />

                                Create First Debit Note
                            </button>

                        )}

                    </div>

                ) : (

                    /* =============================================
                        TABLE
                    ============================================= */

                    <div className="debit-note-table-wrapper">

                        <table className="debit-note-table">

                            <thead>
                                <tr>

                                    <th>
                                        Debit Note
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                    <th>
                                        Reason
                                    </th>

                                    <th className="text-right">
                                        Items
                                    </th>

                                    <th className="text-right">
                                        Debit Amount
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th className="text-right">
                                        Action
                                    </th>

                                </tr>
                            </thead>


                            <tbody>

                                {debitNotes.map(
                                    (
                                        note,
                                        index
                                    ) => {

                                        const status =
                                            getDebitNoteStatus(
                                                note
                                            );

                                        return (
                                            <tr
                                                key={
                                                    note?._id ||
                                                    index
                                                }
                                            >

                                                {/* NUMBER */}

                                                <td>

                                                    <div className="debit-note-document">

                                                        <div className="debit-note-document-icon">
                                                            <FiFileText />
                                                        </div>


                                                        <div className="debit-note-document-content">

                                                            <strong className="debit-note-number">
                                                                {getDebitNoteNumber(
                                                                    note
                                                                )}
                                                            </strong>


                                                            <small className="debit-note-source-number">

                                                                Against{" "}

                                                                {getPurchaseBillNumber(
                                                                    purchaseBill
                                                                )}

                                                            </small>

                                                        </div>

                                                    </div>

                                                </td>


                                                {/* DATE */}

                                                <td>
                                                    {formatDate(
                                                        note
                                                            ?.debitNoteDate ||
                                                        note
                                                            ?.documentDate ||
                                                        note
                                                            ?.createdAt
                                                    )}
                                                </td>


                                                {/* REASON */}

                                                <td>

                                                    <div className="debit-note-reason">

                                                        <strong>
                                                            {formatLabel(
                                                                getDebitNoteReason(
                                                                    note
                                                                )
                                                            )}
                                                        </strong>


                                                        {note?.reasonDetails && (

                                                            <span>
                                                                {
                                                                    note.reasonDetails
                                                                }
                                                            </span>

                                                        )}

                                                    </div>

                                                </td>


                                                {/* ITEMS */}

                                                <td className="text-right">

                                                    <span className="debit-note-item-count">
                                                        {getDebitNoteItemCount(
                                                            note
                                                        )}
                                                    </span>

                                                </td>


                                                {/* AMOUNT */}

                                                <td className="text-right">

                                                    <strong className="debit-note-amount">

                                                        {formatCurrency(
                                                            getDebitNoteAmount(
                                                                note
                                                            )
                                                        )}

                                                    </strong>

                                                </td>


                                                {/* STATUS */}

                                                <td>

                                                    <span
                                                        className={`
                                                        debit-note-status
                                                        debit-note-status-${status.toLowerCase()}
                                                    `}
                                                    >

                                                        {status ===
                                                            "CONFIRMED" ? (

                                                            <FiCheckCircle />

                                                        ) : status ===
                                                            "CANCELLED" ? (

                                                            <FiXCircle />

                                                        ) : (

                                                            <FiClock />

                                                        )}


                                                        {formatLabel(
                                                            status
                                                        )}

                                                    </span>

                                                </td>


                                                {/* ACTION */}

                                                <td className="text-right">
                                                    <button
                                                        type="button"
                                                        className="debit-note-view-button"
                                                        onClick={() =>
                                                            handleView(
                                                                note
                                                            )
                                                        }
                                                    >
                                                        <FiEye />
                                                        View
                                                    </button>
                                                </td>

                                            </tr>
                                        );
                                    }
                                )}

                            </tbody>

                        </table>

                    </div>

                )}

            </div>


            {/* =====================================================
            CREATE DIALOG
        ===================================================== */}

            <CreateDebitNoteDialog
                open={dialogOpen}
                purchaseBill={purchaseBill}
                onClose={() =>
                    setDialogOpen(false)
                }
                onSuccess={
                    handleDebitNoteCreated
                }
            />
            <DebitNoteDetailsDialog
                open={detailsDialogOpen}
                debitNote={selectedDebitNote}
                purchaseBill={purchaseBill}
                onClose={
                    handleCloseDetailsDialog
                }
            />
        </div>

    );
};

export default DebitNotePanel;